/*
 * Hush — gift wrapping switch for the cart drawer and cart page (Berlin 1.3.8).
 *
 * Markup comes from snippets/cart-gift-wrap.liquid. State is never kept on the client: every change
 * re-renders the theme's own cart sections, so the switch always reflects what is really in the cart.
 *
 *  ON  -> POST /cart/add.js  (gift-wrap variant, qty 1) with the same `sections` the theme's
 *         CartItems / CartDrawerItems render, then swap those sections like cart.js does.
 *  OFF -> the theme's own CartItems.updateQuantity(line, 0), which handles loading, re-render,
 *         live region and errors.
 *  NOTE -> POST /cart/change.js { line, quantity, properties: { "Gift note": … } } debounced,
 *         flushed on blur (keepalive). No re-render while typing; the gift line's note text is
 *         updated in place.
 *  ORPHAN -> when a cart change leaves only the gift wrap behind, it is removed as well.
 */
(function () {
  if (customElements.get('cart-gift-wrap')) return;

  var NOTE_DEBOUNCE_MS = 600;
  var hush = (window.HushGiftWrap = window.HushGiftWrap || {});

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(null, args);
      }, wait);
    };
  }

  function jsonConfig() {
    if (typeof fetchConfig === 'function') return fetchConfig();
    return { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
  }

  function cartItemsElement() {
    return document.querySelector('cart-drawer-items') || document.querySelector('cart-items');
  }

  function liveRegion() {
    return document.getElementById('CartDrawer-LiveRegionText') || document.getElementById('cart-live-region-text');
  }

  function announce(message) {
    var region = liveRegion();
    if (!region || !message) return;
    region.textContent = message;
    region.setAttribute('aria-hidden', 'false');
    setTimeout(function () {
      region.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  function installOrphanGuard() {
    if (hush.orphanGuard) return;
    hush.orphanGuard = true;
    document.addEventListener('cart:change', function (event) {
      var cart = event.detail && event.detail.cart;
      var giftId = hush.variantId;
      if (!cart || !Array.isArray(cart.items) || !giftId) return;
      var hasProducts = cart.items.some(function (it) {
        return it.variant_id !== giftId;
      });
      var onlyGift = cart.items.length > 0 && !hasProducts;
      var hadProducts = hush.hasProducts === true;
      hush.hasProducts = hasProducts;
      if (!onlyGift || !hadProducts) return;
      // The product that was being wrapped is gone: drop the orphaned gift wrap once the current
      // update has finished rendering.
      setTimeout(function () {
        var items = cartItemsElement();
        if (items && typeof items.updateQuantity === 'function') {
          hush.announceAfterRender = hush.msgRemoved || '';
          items.updateQuantity('1', 0);
        }
      }, 0);
    });
  }

  class CartGiftWrap extends HTMLElement {
    constructor() {
      super();
      this.source = this.dataset.source || 'drawer';
      this.variantId = Number(this.dataset.variantId);
      this.line = Number(this.dataset.line) || 0;
      this.quantity = Number(this.dataset.quantity) || 1;
      this.isOn = this.dataset.on === 'true';
      this.noteProperty = this.dataset.noteProperty || 'Gift note';
      this.busy = false;
      this.saving = false;
      this.saveAgain = false;

      hush.variantId = this.variantId;
      hush.msgRemoved = this.dataset.msgRemoved || '';
      if (typeof hush.hasProducts !== 'boolean') hush.hasProducts = this.dataset.hasProducts === 'true';

      var self = this;
      this.querySelectorAll('[data-gift-wrap-toggle]').forEach(function (el) {
        el.addEventListener('click', function (event) {
          event.preventDefault();
          self.toggle();
        });
      });

      this.note = this.querySelector('[data-gift-wrap-note]');
      if (this.note) {
        this.lastSavedNote = this.note.value.trim();
        this.saveNoteDebounced = debounce(function () {
          self.saveNote(false);
        }, NOTE_DEBOUNCE_MS);
        this.note.addEventListener('input', function (event) {
          event.stopPropagation();
          self.onNoteInput();
        });
        // CartItems listens for bubbling `change` events and would treat this as a quantity change.
        this.note.addEventListener('change', function (event) {
          event.stopPropagation();
        });
        this.note.addEventListener('blur', function () {
          self.saveNote(true);
        });
      }

      installOrphanGuard();

      // Orphan on render: the cart holds only gift wrap (e.g. the last product was removed by an app that
      // reloads the page, such as Discount Ninja, so no cart:change event reached the guard above).
      if (this.isOn && this.dataset.hasProducts === 'false' && !hush.orphanRemovalInFlight) {
        hush.orphanRemovalInFlight = true;
        hush.announceAfterRender = this.dataset.msgRemoved || '';
        var orphan = this;
        setTimeout(function () {
          orphan.removeWrap();
          hush.orphanRemovalInFlight = false;
        }, 0);
        return;
      }

      if (hush.announceAfterRender) {
        announce(hush.announceAfterRender);
        hush.announceAfterRender = '';
      }
      if (hush.focusAfterRender === this.source) {
        hush.focusAfterRender = '';
        var sw = this.querySelector('[role="switch"]');
        if (sw) {
          var drawer = document.querySelector('cart-drawer');
          if (drawer && typeof trapFocus === 'function') {
            trapFocus(drawer, sw);
          } else {
            sw.focus({ preventScroll: true });
          }
        }
      }
    }

    toggle() {
      if (this.busy) return;
      if (this.isOn) this.removeWrap();
      else this.addWrap();
    }

    setBusy(state) {
      this.busy = state;
      this.classList.toggle('is-loading', state);
      this.querySelectorAll('[data-gift-wrap-toggle]').forEach(function (el) {
        el.setAttribute('aria-busy', state ? 'true' : 'false');
      });
    }

    paint(on) {
      this.classList.toggle('is-on', on);
      var sw = this.querySelector('[role="switch"]');
      if (sw) sw.setAttribute('aria-checked', on ? 'true' : 'false');
    }

    showError(message) {
      var text = message || this.dataset.msgError || '';
      var local = this.querySelector('[data-gift-wrap-error]');
      if (local) local.textContent = text;
      var global = document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
      if (global) global.textContent = text;
    }

    clearError() {
      var local = this.querySelector('[data-gift-wrap-error]');
      if (local) local.textContent = '';
      var global = document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
      if (global) global.textContent = '';
    }

    sectionsToRender() {
      var items = cartItemsElement();
      if (items && typeof items.getSectionsToRender === 'function') {
        try {
          return items.getSectionsToRender();
        } catch (err) {
          return [];
        }
      }
      return [];
    }

    renderSections(sections, html) {
      if (!html) return;
      sections.forEach(function (section) {
        var root = document.getElementById(section.id);
        var markup = html[section.section];
        if (!root || !markup) return;
        var target = root.querySelector(section.selector) || root;
        var doc = new DOMParser().parseFromString(markup, 'text/html');
        var source = doc.querySelector(section.selector);
        target.innerHTML = source ? source.innerHTML : doc.body.innerHTML;
      });
    }

    refreshCartState() {
      return fetch(window.routes.cart_url + '.js')
        .then(function (response) {
          return response.json();
        })
        .then(function (cart) {
          document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart: cart, sectionId: 'cart-gift-wrap' } }));
          return cart;
        })
        .catch(function () {
          return null;
        });
    }

    addWrap() {
      var self = this;
      this.clearError();
      this.setBusy(true);
      this.paint(true);
      var sections = this.sectionsToRender();
      var body = JSON.stringify({
        items: [{ id: this.variantId, quantity: 1 }],
        sections: sections.map(function (s) {
          return s.section;
        }),
        sections_url: window.location.pathname,
      });
      fetch(window.routes.cart_add_url, Object.assign(jsonConfig(), { body: body }))
        .then(function (response) {
          return response.json();
        })
        .then(function (data) {
          if (data.status || data.errors) {
            throw new Error(data.description || data.message || data.errors);
          }
          hush.focusAfterRender = self.source;
          hush.hasProducts = true;
          self.renderSections(sections, data.sections);
          announce(self.dataset.msgAdded || '');
          if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
            // Source 'cart-items' keeps CartItems from re-fetching the page section (which would
            // drop the section id on the cart page); the sections above are already fresh.
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', productVariantId: self.variantId });
          }
          return self.refreshCartState();
        })
        .catch(function () {
          self.paint(false);
          self.setBusy(false);
          self.showError();
        });
    }

    removeWrap() {
      if (!this.line) return;
      var items = cartItemsElement();
      this.clearError();
      this.setBusy(true);
      this.paint(false);
      hush.announceAfterRender = this.dataset.msgRemoved || '';
      hush.focusAfterRender = this.source;
      if (items && typeof items.updateQuantity === 'function') {
        items.updateQuantity(String(this.line), 0);
        return;
      }
      // Fallback (no CartItems on the page): plain change + reload of the sections.
      var self = this;
      var sections = this.sectionsToRender();
      fetch(window.routes.cart_change_url, Object.assign(jsonConfig(), {
        body: JSON.stringify({
          line: this.line,
          quantity: 0,
          sections: sections.map(function (s) {
            return s.section;
          }),
          sections_url: window.location.pathname,
        }),
      }))
        .then(function (response) {
          return response.json();
        })
        .then(function (cart) {
          if (cart.status || cart.errors) throw new Error(cart.description || cart.errors);
          self.renderSections(sections, cart.sections);
          document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart: cart, sectionId: 'cart-gift-wrap' } }));
        })
        .catch(function () {
          self.paint(true);
          self.setBusy(false);
          self.showError();
        });
    }

    onNoteInput() {
      if (!this.note) return;
      var max = Number(this.dataset.noteMax) || 200;
      if (this.note.value.length > max) this.note.value = this.note.value.slice(0, max);
      var counter = this.querySelector('[data-gift-wrap-count-value]');
      if (counter) counter.textContent = String(this.note.value.length);
      this.clearError();
      this.saveNoteDebounced();
    }

    saveNote(flush) {
      if (!this.note || !this.line) return;
      if (this.saving) {
        this.saveAgain = true;
        return;
      }
      var value = this.note.value.trim();
      if (value === this.lastSavedNote) return;
      var self = this;
      var properties = {};
      properties[this.noteProperty] = value;
      this.saving = true;
      fetch(window.routes.cart_change_url, Object.assign(jsonConfig(), {
        body: JSON.stringify({ line: this.line, quantity: this.quantity, properties: properties }),
        keepalive: !!flush,
      }))
        .then(function (response) {
          return response.json();
        })
        .then(function (cart) {
          if (cart.status || cart.errors) throw new Error(cart.description || cart.errors);
          self.lastSavedNote = value;
          var index = cart.items.findIndex(function (it) {
            return it.variant_id === self.variantId;
          });
          if (index >= 0) {
            var newLine = index + 1;
            if (newLine !== self.line) self.relabelLine(self.line, newLine);
            self.line = newLine;
            self.quantity = cart.items[index].quantity || self.quantity;
          }
          self.updateLineNote(value);
        })
        .catch(function () {
          self.showError();
        })
        .then(function () {
          self.saving = false;
          if (self.saveAgain) {
            self.saveAgain = false;
            self.saveNote(false);
          }
        });
    }

    giftLineElement(line) {
      return document.getElementById('CartDrawer-Item-' + line) || document.getElementById('CartItem-' + line);
    }

    relabelLine(oldLine, newLine) {
      var el = this.giftLineElement(oldLine);
      if (!el) return;
      el.id = el.id.replace(String(oldLine), String(newLine));
      el.querySelectorAll('[data-index]').forEach(function (node) {
        node.dataset.index = String(newLine);
      });
      el.querySelectorAll('[id]').forEach(function (node) {
        node.id = node.id.replace('-' + oldLine, '-' + newLine);
      });
    }

    updateLineNote(value) {
      var el = this.giftLineElement(this.line);
      if (!el) return;
      var wrap = el.querySelector('[data-gift-wrap-line-note-wrap]');
      var text = el.querySelector('[data-gift-wrap-line-note]');
      if (text) text.textContent = value;
      if (wrap) wrap.hidden = value.length === 0;
    }
  }

  customElements.define('cart-gift-wrap', CartGiftWrap);
})();
